import { useState } from 'react';
import { motion } from 'framer-motion';
import { Parameter, ParameterType } from '../../api/types';

interface ParameterControlProps {
  parameter: Parameter;
  onChange: (value: boolean | number | string) => Promise<void>;
  disabled?: boolean;
  minValue?: number;
  maxValue?: number;
  step?: number;
}

export function ParameterControl({
  parameter,
  onChange,
  disabled = false,
  minValue = 0,
  maxValue = 100,
  step = 1
}: ParameterControlProps) {
  const [isLoading, setIsLoading] = useState(false);

  const handleChange = async (value: boolean | number | string) => {
    if (disabled || isLoading) return;
    setIsLoading(true);
    try {
      await onChange(value);
    } finally {
      setIsLoading(false);
    }
  };

  if (parameter.type === ParameterType.Bool) {
    return (
      <BoolControl
        value={parameter.boolValue ?? false}
        onChange={handleChange}
        disabled={disabled || isLoading}
        label={parameter.rusName || parameter.name}
      />
    );
  }

  if (parameter.type === ParameterType.Numeric) {
    return (
      <NumericControl
        value={parameter.numericValue ?? minValue}
        onChange={handleChange}
        disabled={disabled || isLoading}
        minValue={minValue}
        maxValue={maxValue}
        step={step}
        label={parameter.rusName || parameter.name}
        parameterName={parameter.name}
      />
    );
  }

  if (parameter.type === ParameterType.Enum) {
    return (
      <EnumControl
        value={parameter.stringValue ?? ''}
        onChange={handleChange}
        disabled={disabled || isLoading}
        label={parameter.rusName || parameter.name}
        parameterName={parameter.name}
      />
    );
  }

  return (
    <div className="bg-slate-50/50 rounded-xl p-4">
      <span className="text-slate-500 text-sm">{parameter.rusValueOnlyView || '—'}</span>
    </div>
  );
}

interface BoolControlProps {
  value: boolean;
  onChange: (value: boolean) => void;
  disabled: boolean;
  label: string;
}

function BoolControl({ value, onChange, disabled, label }: BoolControlProps) {
  return (
    <div className="bg-slate-50/50 rounded-xl p-4">
      <div className="flex items-center justify-between">
        <span className="font-medium text-slate-700">{label}</span>
        <button
          onClick={() => onChange(!value)}
          disabled={disabled}
          className={`
            relative w-14 h-8 rounded-full transition-all duration-300
            ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}
            ${value 
              ? 'gradient-primary shadow-lg shadow-indigo-500/30' 
              : 'bg-slate-200'
            }
          `}
        >
          <motion.div
            className="absolute top-1 w-6 h-6 bg-white rounded-full shadow-md"
            animate={{ left: value ? '1.75rem' : '0.25rem' }}
            transition={{ type: 'spring', stiffness: 500, damping: 30 }}
          />
        </button>
      </div>
    </div>
  );
}

interface NumericControlProps {
  value: number;
  onChange: (value: number) => void;
  disabled: boolean;
  minValue: number;
  maxValue: number;
  step: number;
  label: string;
  parameterName: string;
}

function NumericControl({
  value,
  onChange,
  disabled,
  label,
  parameterName
}: NumericControlProps) {
  const [localValue, setLocalValue] = useState(value);

  const getRange = () => {
    if (parameterName.includes('temperature')) return { min: 16, max: 30, unit: '°C' };
    if (parameterName.includes('brightness')) return { min: 0, max: 100, unit: '%' };
    if (parameterName.includes('position')) return { min: 0, max: 100, unit: '%' };
    if (parameterName.includes('speed')) return { min: 1, max: 3, unit: '' };
    return { min: 0, max: 100, unit: '' };
  };

  const { min, max, unit } = getRange();
  const percentage = ((localValue - min) / (max - min)) * 100;

  const handleCommit = () => {
    if (localValue !== value) onChange(localValue);
  };

  return (
    <div className="bg-slate-50/50 rounded-xl p-4">
      <div className="flex items-center justify-between mb-4">
        <span className="font-medium text-slate-700">{label}</span>
        <motion.span 
          key={localValue}
          initial={{ scale: 1.1 }}
          animate={{ scale: 1 }}
          className="text-lg font-bold text-gradient"
        >
          {localValue}{unit}
        </motion.span>
      </div>
      
      <div className="relative h-2">
        <div className="absolute inset-0 bg-slate-200/60 rounded-full" />
        <motion.div
          className="absolute inset-y-0 left-0 gradient-primary rounded-full"
          initial={false}
          animate={{ width: `${percentage}%` }}
        />
        
        <input
          type="range"
          min={min}
          max={max}
          step={1}
          value={localValue}
          onChange={(e) => setLocalValue(Number(e.target.value))}
          onMouseUp={handleCommit}
          onTouchEnd={handleCommit}
          disabled={disabled}
          className="absolute inset-0 w-full opacity-0 cursor-pointer disabled:cursor-not-allowed"
        />
        
        <motion.div
          className="absolute top-1/2 -translate-y-1/2 w-6 h-6 bg-white rounded-full shadow-lg border-2 border-indigo-400 pointer-events-none"
          initial={false}
          animate={{ left: `calc(${percentage}% - 12px)` }}
        />
      </div>
      
      <div className="flex justify-between mt-2 text-xs text-slate-400">
        <span>{min}{unit}</span>
        <span>{max}{unit}</span>
      </div>
    </div>
  );
}

interface EnumControlProps {
  value: string;
  onChange: (value: string) => void;
  disabled: boolean;
  label: string;
  parameterName: string;
}

function EnumControl({ value, onChange, disabled, label, parameterName }: EnumControlProps) {
  const getOptions = (): { value: string; label: string }[] => {
    if (parameterName === 'mode') {
      return [
        { value: 'cool', label: '❄️ Охлаждение' },
        { value: 'heat', label: '🔥 Нагрев' },
        { value: 'auto', label: '⚡ Авто' },
        { value: 'fan', label: '💨 Вентиляция' },
        { value: 'comfort', label: '✨ Комфорт' },
        { value: 'eco', label: '🌿 Эко' },
      ];
    }
    if (parameterName === 'fan_speed' || parameterName === 'speed') {
      return [
        { value: '1', label: 'Низкая' },
        { value: '2', label: 'Средняя' },
        { value: '3', label: 'Высокая' },
      ];
    }
    return [];
  };

  const options = getOptions();

  return (
    <div className="bg-slate-50/50 rounded-xl p-4">
      <span className="font-medium text-slate-700 block mb-3">{label}</span>
      
      <div className="flex flex-wrap gap-2">
        {options.map((option) => (
          <motion.button
            key={option.value}
            whileTap={{ scale: 0.97 }}
            onClick={() => onChange(option.value)}
            disabled={disabled}
            className={`
              px-4 py-2.5 rounded-xl text-sm font-medium transition-all
              ${disabled ? 'opacity-40 cursor-not-allowed' : 'cursor-pointer'}
              ${value === option.value
                ? 'gradient-primary text-white shadow-lg shadow-indigo-500/30'
                : 'bg-white text-slate-600 hover:bg-slate-50 border border-slate-100'
              }
            `}
          >
            {option.label}
          </motion.button>
        ))}
      </div>
    </div>
  );
}
